import clsx from 'clsx'
import React, { useEffect, useRef, useState } from 'react'

type Props = {
    children: React.ReactNode | React.ReactNode[]
    className?: string
    rowHeight?: number
    maxRows?: number // if undefined we'll show all items
}

export default function Cards({
    children,
    className,
    rowHeight = 140,
    maxRows,
}: Props): React.JSX.Element {
    const containerRef = useRef<HTMLDivElement>(null)
    const [isExpanded, setIsExpanded] = useState(false)
    const [shouldClamp, setShouldClamp] = useState(false)

    useEffect(() => {
        if (!maxRows) {
            return
        }

        const el = containerRef.current
        if (!el) return

        const checkShouldClamp = () => {
            const maxHeight = rowHeight * maxRows
            setShouldClamp(el.scrollHeight > maxHeight)
        }

        checkShouldClamp()
        window.addEventListener('resize', checkShouldClamp)
        return () => window.removeEventListener('resize', checkShouldClamp)
    }, [children, rowHeight, maxRows])

    const containerClasses = clsx(
        'flex flex-wrap gap-4 justify-start transition-all duration-300',
        !isExpanded && 'overflow-hidden',
        className,
    )

    const containerStyle =
        !isExpanded && maxRows !== undefined
            ? { maxHeight: `${rowHeight * maxRows}px` }
            : {}

    return (
        <div className="w-full">
            <div
                ref={containerRef}
                className={containerClasses}
                style={containerStyle}
            >
                {children}
            </div>

            {shouldClamp && (
                <div className="mt-4 flex justify-center w-full">
                    <button
                        className="text-sm text-blue-600 underline hover:text-blue-800"
                        onClick={() => setIsExpanded((prev) => !prev)}
                    >
                        {isExpanded ? 'Hide' : 'Show More'}
                    </button>
                </div>
            )}
        </div>
    )
}
